import { unwrap } from "@sagot/api-client";
import { API_HEADERS } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, EmptyState, ErrorState, Icon, Skeleton, toast } from "@sagot/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { AuthLayout } from "../../../../auth/components";
import { useErrorMessage } from "../../../../lib/api-errors";
import { integrationsHref } from "../../../../lib/booking-links";
import { useApi } from "../../../../session/session-provider";
import { wsKeys } from "../../shared";
import { clearPendingConnect, readPendingConnect, type GoogleConnectIntegration, type PendingGoogleConnect } from "./pending-connect";

/** Google OAuth redirect target (backend `REDIRECT_PATH` in `app/services/calendars/connect.py`). */
export const GOOGLE_CALLBACK_PATH = "/integrations/google/callback";

/** Copy and icon per Google integration finishing on this shared callback URL. */
const FLOWS: Readonly<Record<GoogleConnectIntegration, { copy: string; icon: "calendar" | "table" }>> = {
  google_calendar: { copy: "settings.integrations.google.callback", icon: "calendar" },
  google_sheets: { copy: "settings.integrations.sheets.callback", icon: "table" },
};
const MAX_PARAM = 4096;

function copyOf(pending: PendingGoogleConnect | null): string {
  return FLOWS[pending?.integration ?? "google_calendar"].copy;
}

function backHref(pending: PendingGoogleConnect | null): string {
  return pending ? integrationsHref(pending.wsSlug) : "/";
}

function BackButton({ pending }: { pending: PendingGoogleConnect | null }) {
  const { t } = useTranslation();
  const P = copyOf(pending);
  return (
    <Button asChild variant="secondary">
      <AppLink href={backHref(pending)}>
        <Icon name="arrow-left" />
        {t(pending ? `${P}.back` : `${P}.backHome`)}
      </AppLink>
    </Button>
  );
}

function useGoogleExchange(pending: PendingGoogleConnect, code: string, state: string) {
  const api = useApi();
  const init = { params: { header: { [API_HEADERS.workspace]: pending.wsId } }, body: { code, state } };
  return useQuery({
    // The OAuth state is single-use server-side: never refetch or retry this call.
    queryKey: ["google-connect-callback", pending.integration, state],
    queryFn: () =>
      unwrap(
        pending.integration === "google_sheets"
          ? api.POST("/v1/integrations/google_sheets/callback", init)
          : api.POST("/v1/integrations/google_calendar/callback", init),
      ),
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

function ConnectFlow({ pending, code, state }: { pending: PendingGoogleConnect; code: string; state: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const client = useQueryClient();
  const message = useErrorMessage();
  const exchange = useGoogleExchange(pending, code, state);
  const P = copyOf(pending);
  const finished = useRef(false);
  useEffect(() => {
    if (!exchange.isSuccess || finished.current) return;
    finished.current = true;
    clearPendingConnect();
    void client.invalidateQueries({ queryKey: wsKeys.integrations(pending.wsId) });
    toast.success(t(`${P}.connected`));
    void navigate(backHref(pending), { replace: true });
  }, [exchange.isSuccess, client, navigate, pending, t, P]);

  if (exchange.isError) {
    return (
      <div className="space-y-4">
        <ErrorState title={t(`${P}.failedTitle`)} description={message(exchange.error) || t(`${P}.failedBody`)} />
        <div className="flex justify-center">
          <BackButton pending={pending} />
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-3" aria-busy="true">
      <p className="text-sm text-fg-muted">{t(`${P}.loading`)}</p>
      <Skeleton className="h-16 w-full" />
    </div>
  );
}

function readParam(params: URLSearchParams, key: string): string | null {
  const value = params.get(key);
  return value && value.length <= MAX_PARAM ? value : null;
}

/** `/integrations/google/callback?code&state` — finish the Google Calendar or Sheets connect started in Settings. */
export function GoogleCallbackPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const [pending] = useState(() => readPendingConnect());
  const P = copyOf(pending);
  const icon = FLOWS[pending?.integration ?? "google_calendar"].icon;
  const code = readParam(params, "code");
  const state = readParam(params, "state");
  const body = params.has("error") ? (
    <EmptyState icon="x-circle" title={t(`${P}.deniedTitle`)} description={t(`${P}.deniedBody`)} action={<BackButton pending={pending} />} />
  ) : !pending || !code || !state ? (
    <EmptyState icon="clock" title={t(`${P}.missingTitle`)} description={t(`${P}.missingBody`)} action={<BackButton pending={pending} />} />
  ) : (
    <ConnectFlow pending={pending} code={code} state={state} />
  );
  return (
    <AuthLayout title={t(`${P}.docTitle`)} width="md">
      <div className="space-y-5">
        <h1 className="flex items-center gap-2 text-2xl font-extrabold tracking-tight">
          <Icon name={icon} className="size-6 text-primary-ink" />
          {t(`${P}.title`)}
        </h1>
        {body}
      </div>
    </AuthLayout>
  );
}
